<script>
   /*************************************************************/
   /* AppContainer for training                                 */
   /* -------------------------                                 */
   /* app container for training of DD-SIMCA model              */
   /*************************************************************/

   import { untrack } from 'svelte';
   import { Container, Button } from 'svelte-controls-basic';
   import { downloadResults, downloadModel, predict } from '@mdatools-apps/shared/methods/simca.js';

   import { seq, app_residuals } from '@mdatools-apps/shared/utils';
   import { pcamodel_get, pcares_get } from '@mdatools-apps/wasm/pca.js';


   import Outliers from '@mdatools-apps/shared/widgets/Outliers.svelte';
   import Contributions from '@mdatools-apps/shared/components/Contributions.svelte';
   import PrepWidget from '@mdatools-apps/shared/components/prep/PrepWidget.svelte';

   import DataLoader from '@mdatools-apps/shared/components/pca/DataLoader.svelte';
   import ModelCalculator from '@mdatools-apps/shared/components/pca/ModelCalculator.svelte';
   import PCVCalculator from '@mdatools-apps/shared/components/pca/PCVCalculator.svelte';

   import ModelTable from '../shared/ModelTable.svelte';
   import SIMCASettings from '../shared/SIMCASettings.svelte';

   import ResultTable from './ResultTable.svelte';
   import AppPlots from './AppPlots.svelte';

   // WASM promise and session object from parent app
   let {WASM, backupSession, session = $bindable()} = $props();

   // get part of the session for this tab
   const s = $derived(session['train']);


   // properties for showing contributions
   let selected = $state(-1);
   let contributionIsShown = $state(false);


   // callback function to set object with a give position in plot series
   // as outlier
   function markOutlier(pos) {

      // check that position is valid
      if (pos < 0 || pos > s.calres.pca.nrows - 1) return;

      // find sample ID corresponding to the position
      // this is needed if some of the objects already
      // removed from results as outliers
      const id = s.calres.pca.objindices[pos];

      // add the object to outliers array and reset the model and all related outcomes
      if (id >= 0 && !s.outliers.includes(id)) {
         s.outliers.push(id);
         s.model = null;
         s.simca = null;
         s.pvres.simca = null;
         s.pvres.pca = null;
         s.calres.simca = null;
         s.calres.pca = null;
         invalidatePrep();
         s.pvset = null;
      }
   }

   // the preprocessing model is only rebuilt from the data when the user created it here. one
   // loaded from a file has nothing to restore it, so dropping it whenever the excluded objects
   // change forced the user to pick the json again after every exclusion. 'calsetp' goes with it
   // because the widget replaces the two together; the preprocessed set stays valid either way:
   // the excluded objects are applied when the model is fitted, not by taking rows out of it
   function invalidatePrep() {
      if (s.prepType === 'create') {
         s.calsetp = null;
         s.prepmodel = null;
      }
   }

   // callback to reset outliers
   function resetOutliers() {
      s.outliers = [];
      s.model = null;
      s.pvres.simca = null;
      s.pvres.pca = null;
      s.calres.simca = null;
      s.calres.pca = null;
      invalidatePrep();
      s.pvset = null;
   }

   // callback to undo outliers
   function undoOutliers() {
      s.outliers.pop();
      s.model = null;
      s.pvres.simca = null;
      s.pvres.pca = null;
      s.calres.simca = null;
      s.calres.pca = null;
      invalidatePrep();
      s.pvset = null;
   }

   // show modal dialog with contributions for selected object
   function showContributions(pos) {
      if (pos < 0) return;
      selected = pos;
      contributionIsShown = true;
   }

   // hide modal dialog with contributions
   function hideContributions() {
      contributionIsShown = false;
      selected = -1;
   }



   let ind = $derived(s.calres.pca ? {members: seq(0, s.calres.pca.nrows - 1), strangers: []} : null);

   // pre-rendering effect to avoid bad structure of the session object
   $effect.pre(() => {

      // if training set is set null - reset model and all related parameters
      if (s.calset === null) {
         s.model = null;
         s.calsetp = null;
         s.className = null;
         s.outliers = [];
         s.pvset = null;
         s.prepmodel = null;
         s.prepobj = [];
         s.prepType = null
      } else if (s.calset && !s.className) {
         s.className = s.calset.classNames[0];
      }

      // if model object is set to null - reset dependent objects
      if (s.model === null) {
         s.calres.simca = null;
         s.simca = null;
         s.calres.pca = null;
         s.pvset = null;
      }

      // if pvset is set null - reset the corresponding result object
      if (s.pvset === null) {
         s.pvres.simca = null;
         s.pvres.pca = null;
      }
   });

   // if training set is set null - reset model and all related parameters
   $effect(() => {
      if (s.calset === null) {
         s.calsetp = null;
      }
   });

   // if preprocessed training set is set null - reset model and all related parameters
   $effect(() => {
      if (s.calsetp === null) {
         s.simca = null;
         s.pvset = null;
         s.model = null;
      }
   });

   // if model object is set to null - reset dependent objects
   $effect(() => {
      if (s.model === null) {
         s.pvres = {pca: null, simca: null};
         s.calres = {pca: null, simca: null};
         s.pvset = null;
      }
   });

   // if pvset is set null - reset the corresponding result object
   $effect(() => {
      if (s.pvset === null) {
         s.pvres.simca = null;
         s.pvres.pca = null;
      }
   });

   // recalculate SIMCA results for calibration set if eCrit/oCrit were changed
   $effect(() => {
      if (s.calres.pca && s.simca && s.simca.eCrit && s.simca.oCrit && ind) {
         s.calres.simca = predict(s.calres.pca, untrack(() => s.simca.limType), s.simca.eCrit, s.simca.oCrit, ind)
      }
   });

   // recalculate SIMCA results for PV-set if eCrit/oCrit were changed
   $effect(() => {
      if (s.pvres.pca && s.simca && ind) {
         s.pvres.simca = predict(s.pvres.pca, untrack(() => s.simca.limType), s.simca.eCrit, s.simca.oCrit, ind)
      }
   });

   // reactive effect for backing up the session. the session is read by 'backupSession' when the
   // write is due, not here, and the first run is skipped: it only ever coincides with a session
   // the app was given as a whole, which is already on disk, is written by 'MainApp' itself, or is
   // a fresh one that need not be stored. the values after the function are the dependencies (see
   // 'backupSession'): the LENGTH of the outlier list, not the array, because reading a property
   // registers its identity and not its contents, and undo pops in place. without it an undo or
   // reset which follows an exclusion saves nothing, because every other field it writes is
   // already null and fires no change. optional because 'checkSession' tests for the key and not
   // its value, so a session file can carry 'outliers: null', and a throw here would stop this
   // tab saving at all. 'calsetp' is bound by 'PrepWidget' here but was not listed, so a
   // preprocessing change on its own reached nothing and was not stored. nimca's training tab
   // omits it too and does not need it: its settings component refits AUTOMATICALLY once a new
   // 'calsetp' lands and reassigns the listed 's.nimca', whereas this tab refits only from the
   // 'Create model' button, so nothing here reassigns a listed field on its own
   let mounted = false;
   $effect(() => {
      const deps = s.simca ?
         [s.model, s.calset, s.calsetp, s.pvset, s.simca.ncomp, s.pvres.simca, s.outliers?.length] :
         [s.model, s.calset, s.calsetp, s.pvset, s.simca, s.pvres.simca, s.outliers?.length];
      if (mounted) backupSession(() => session, ...deps);
      mounted = true;
   });
</script>

{#if contributionIsShown && s.simca && s.calres.pca && s.pvres.pca}
   <Contributions
      model_getter={pcamodel_get}
      res_getter={pcares_get}
      model={s.model}
      res={{'cal': s.calres.pca, 'pv': s.pvres.pca}}
      residuals={(id) => WASM().then((M) => id === 'cal' ?
         app_residuals(M, s.model, s.calsetp, s.calres.pca) :
         app_residuals(M, s.model, s.pvset, s.pvres.pca))}
      closeModal={hideContributions}
      ncomp={s.simca.ncomp}
      {selected}
   />
{/if}

<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="data-loader">
         <DataLoader bind:dataset={s.calset} className="Class" classMandatory={true} classUnique={true}/>
      </div>

      {#if s.outliers && s.outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers outliers={s.outliers} dataset={s.calset} {resetOutliers} {undoOutliers} />
      </div>
      {/if}

      {#if s.calset }
         <PrepWidget
            {WASM}
            rawdata={s.calset}
            outliers={s.outliers}
            bind:prepType={s.prepType}
            bind:prepobj={s.prepobj}
            bind:prepdata={s.calsetp}
            bind:prepmeta={s.prepmeta}
            bind:prepmodel={s.prepmodel}
         />
      {/if}


      {#if s.calsetp }
      <div class="model-calculator">
         <ModelCalculator
            {WASM}
            dataset={s.calsetp}
            outliers={s.outliers}
            bind:model={s.model}
            bind:resobj={s.calres.pca}
         />
      </div>
      {/if}

      {#if s.model && s.calsetp && s.calres.pca}
      <div class="pcv-calculator">
         <PCVCalculator
            {WASM}
            calset={s.calsetp}
            outliers={s.outliers}
            model={s.model}
            calres={s.calres.pca}
            bind:pvset={s.pvset}
            bind:pvres={s.pvres.pca}
         />
      </div>
      {/if}

      <div class="simca-settings">

         {#if s.model && s.pvres.pca && s.calres.pca}
         <SIMCASettings
            model={s.model}
            className={s.className}
            bind:simca={s.simca}
         />
         {/if}

         {#if s.model && s.pvres.simca && s.calres.simca}
         <Container label='Download'>
            <div class="buttons">
            <Button onclick={() => downloadResults(s.model, s.simca, s.calres, s.calset, "train")} text="Res (cal)"/>
            <Button onclick={() => downloadResults(s.model, s.simca, s.pvres, s.pvset, "PV")} text="Res (pv)" />
            <Button onclick={() => downloadModel(s.model, s.simca, s.prepmodel)} text="Model" />
            </div>
         </Container>
         {/if}
      </div>

      {#if s.simca && s.pvres.simca && s.calres.simca}
      <div class="model-stats">
         <ModelTable model={s.model} simca={s.simca} />
      </div>
      <div class="result-stats">
         <ResultTable calres={s.calres.simca} pvres={s.pvres.simca} ncomp={s.simca.ncomp}/>
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !s.pvres.simca || !s.calres.simca}
         <div class="help-text">
         {#if !s.calset}
            <p>
               In this tab you can train a DD-SIMCA model and optimize its parameters using Procrustes cross-validation. After creating the model you can save it to a file and use it in other tabs. For example, validate and tune it using test set (tab <strong>Test</strong>) or apply it to a dataset with unknown class membership (tab <strong>Predict</strong>).
            </p>
            <p><strong>Step 1.</strong></p>
            <p>Load your training set from a CSV file. The file should contain data as numeric values delimited
               by comma (with dot as decimal separator) or by semicolon (with comma as decimal separator). Rows correspond to observations or objects, columns — to variables.
            </p>
            <p>
               First column must contain class names for each row. All rows in the training set should belong to the target class, so it is expected that the class names are the same (you will get error message if not).
            </p>
            <p>
               First row can contain variable names (<strong>header</strong>) and first column can contain labels for the objects/observations (<strong>row labels</strong>) — just set the corresponding options after selecting the file. In this case it is assumed that column with the class names is located next to the column with row labels. Here is an example:
            </p>
            <div>
               <table>
               <thead>
               <tr>
                  <td>Name</td>
                  <td>Class</td>
                  <td>x1</td>
                  <td>x2</td>
                  <td>x3</td>
               </tr>
               </thead>
               <tbody>
               <tr>
                  <td>O1</td>
                  <td>A</td>
                  <td>3.8</td>
                  <td>1.3</td>
                  <td>13.6</td>
               </tr>
               <tr>
                  <td>O2</td>
                  <td>A</td>
                  <td>11.1</td>
                  <td>12.3</td>
                  <td>3.3</td>
               </tr>
               <tr>
                  <td>O3</td>
                  <td>A</td>
                  <td>1.2</td>
                  <td>2.3</td>
                  <td>4.2</td>
               </tr>
               </tbody>
               </table>
            </div>
            <p></p>
         {:else}
            {#if !s.model}
            <p><strong>Step 2</strong></p>
            <p>
               Next step is to create a global PCA model for the calibration set, which will be the basis for the SIMCA model. Use more components/latent variables (PCs) than you expect to be optimal. You will have possibility to set
               optimal number later.
            </p>
            {/if}

            {#if s.model && !s.pvset}
            <p><strong>Step 3</strong></p>
            <p>
               Now set necessary parameters for Procrustes cross-validation and click button to compute the PV-set, which will help you to optimize SIMCA model parameters. The parameters are the same as for conventional cross-validation: type of splits and number of segments. It is recommended to use fewer number of segments (e.g. 3, 4 or 5).
            </p>
            <p>
               After this step SIMCA model will be created automatically and you will be able to tune its parameters.
            </p>
            {/if}
         {/if}
         </div>
      {:else if s.model && s.simca && s.pvres.pca }
         {#key s.pvres.pca }
         <AppPlots {markOutlier} {showContributions} {...s} />
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      border-radius: 0.25rem;
   }

   .app-controls > .model-stats,
   .app-controls > .result-stats {
      background: #e8e8e8;
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }


   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p, .help-text > div {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text table {
      border-collapse: collapse;
   }

   .help-text table td{
      padding: 0.25em 0.5em;
      background: #e0f0fa;
      text-align: right;
      border: 1px solid white;
   }

   .help-text table thead tr td{
      font-weight: bold;
      background: #f8f4e0;
   }

   .help-text table td:first-child {
      font-weight: bold;
      background: #f8f4e0;
      padding-right: 1em;
   }

   .help-text table tbody td:nth-child(2) {
      background: #e0e8f0;
      padding-right: 1em;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }



   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(325px, 30%) 1fr;
      }
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

</style>
